import type { CSSProperties } from 'react';

/**
 * 列表頁外框共用樣式(R412 前端健檢抽出)。
 * -----------------------------------------------------------------------------
 * 專案列表(`projectListStyles`)與品牌規範列表(`brandKitListStyles`)是同一份 D3 列表頁
 * 版型,原本兩邊各抄一份、14 個常數逐字相同 —— 改一邊另一邊不會跟著動,遲早漂移。
 * 這裡只收**兩頁真的相同**的外框(麵包屑 / 頁頭 / 篩選列 / 搜尋框 / 排序 / 主按鈕 / 建立虛線格
 * 的 + 方塊 / 錯誤橫幅 / 載入中);兩頁數值不同的(副標 / 搜尋框外框 / 格網 / 虛線格 / 列表)
 * 留在各自檔案,不硬合。兩支舊檔 re-export,呼叫端 import 路徑不變。
 */

export const breadcrumbStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--ink-500)',
  marginBottom: 8,
};
export const breadcrumbLinkStyle: CSSProperties = {
  color: 'var(--ink-500)',
  textDecoration: 'none',
};
export const breadcrumbSepStyle: CSSProperties = {
  color: 'var(--ink-700)',
  fontSize: 11,
};
export const breadcrumbCurrentStyle: CSSProperties = {
  color: 'var(--ink-300)',
  fontWeight: 500,
};
export const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginBottom: 24,
};
export const titleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--ink-100)',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.02em',
};
export const filterBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 20,
};
/** D3 `.pj-search .ic` —— 放大鏡是圖示(次文字色),不是文字。 */
export const searchIconStyle: CSSProperties = {
  display: 'flex',
  flexShrink: 0,
  color: 'var(--ink-500)',
};
export const searchInputStyle: CSSProperties = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: 14,
  color: 'var(--ink-100)',
};
/**
 * 排序下拉(R393:原生 `<select>` → `HaiSelect` 工具列版)。寬度照 D3 `.pj-sortbtn`
 * 的 `width:168px; flex:0 0 168px` —— 選項長短不同時按鈕不跟著伸縮,頂列不跳。
 */
export const sortSelectStyle: CSSProperties = {
  width: 168,
  flex: '0 0 168px',
};
export const dashedTilePlusBoxStyle: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 999,
  background: 'var(--ink-850)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
};
/**
 * CIS 頁面主行動 `.btn-solid`(高 34 / padding 0 14 / 圓角 8 / 12.5px 700;底 `--btn-solid`
 * —— 主色 #0D9488 配白字只有 3.74:1)。⚠️ **只給 `<Link>` 用**(空態 CTA):`<button>`
 * 一律掛 `className="hai-btn-solid"`(hover / 停用態在 global.css,inline 會蓋掉 :hover)。
 */
export const primaryBtnStyle: CSSProperties = {
  // `<a>` 載體 → `fontFamily` 要明寫
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  height: 34,
  padding: '0 14px',
  background: 'var(--btn-solid)',
  color: 'var(--on-accent)',
  border: 'none',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
/** 錯誤 = 玫瑰(CIS:淡底 rose-50 / 框 rose-200 / 字 rose-700;`--err` 只是色相識別)。 */
export const errorBannerStyle: CSSProperties = {
  marginBottom: 24,
  padding: '12px 16px',
  background: 'var(--rose-50)',
  border: '1px solid var(--rose-200)',
  borderRadius: 'var(--r-sm)',
  fontSize: 'var(--text-sm)',
  color: 'var(--rose-700)',
};
export const loadingStyle: CSSProperties = {
  padding: '40px 0',
  textAlign: 'center',
  color: 'var(--ink-400)',
  fontSize: 'var(--text-md)',
};
